import { UserId } from '@candulabs/core';
import React, { useEffect, useMemo, useRef, useState } from 'react';

import { ContentType } from '@candu-ai/content/types';
import { DocumentDefinitionType } from '@candu-ai/document/types';
import { BuiltInComponents, isTooltipDocument } from 'craft/constants/schema';

import { ContentThumbnailWithCarousel, EmptyPattern } from './ContentThumbnailWithCarousel';
import { getOverrideTooltipWrapperStyles, getTooltipMountPosition } from './getTooltipPosition';

import { DocumentScreenshot } from '../DocumentScreenshot';

const adjustPlacementsToPreview = (placements: any) => {
  return Object.keys(placements || {}).reduce(
    (prev, curr) => ({
      ...prev,
      [curr]: {
        selector: 'div',
      },
    }),
    {},
  );
};

interface Props {
  clientToken: string;
  userId?: UserId;
  document?: DocumentDefinitionType;
  traits?: any;
  slug?: string;
  disableSegmentMembership?: boolean;
  disableClientConfig?: boolean;
  checkeredBackdrop?: boolean;
  hotspotLimit?: number;
  stylesheetFromSource?: boolean;
}

interface ScreenshotProps {
  clientToken: string;
  document?: DocumentDefinitionType;
  checkeredBackdrop?: boolean;
  hotspotLimit?: number;
  nodeId?: string;
}

const CanduContentFrameScreenshot = ({
  clientToken,
  document,
  checkeredBackdrop = true,
  hotspotLimit = 3,
  nodeId,
}: ScreenshotProps) => {
  const rootNode = document?.document.nodes[document?.document.rootNodeId];
  const rootNodeType = rootNode?.type;
  const isTypeWithTooltips =
    rootNodeType === BuiltInComponents.HotspotGroup || rootNodeType === BuiltInComponents.Tour;

  const tooltipNodeIds = useMemo(() => {
    if (!isTypeWithTooltips || !rootNode?.nodes?.length) {
      return undefined;
    }

    return rootNode.nodes.slice(0, hotspotLimit);
  }, [rootNode?.nodes, hotspotLimit, isTypeWithTooltips]);

  return (
    <DocumentScreenshot
      clientToken={clientToken}
      checkeredBackdrop={checkeredBackdrop}
      versionId={document?.versionId}
      rootNodeId={nodeId}
      rootNodeType={rootNodeType as BuiltInComponents}
      tooltipNodeIds={tooltipNodeIds}
    />
  );
};

const CanduContentFrameInteractive = ({
  clientToken,
  userId,
  document,
  traits,
  slug,
  stylesheetFromSource = true,
  disableSegmentMembership = true,
  disableClientConfig = true,
  checkeredBackdrop = true,
  hotspotLimit = 3,
}: Props) => {
  const embedComponent = useRef<any>(null);
  const Client = new (window as any).CanduClient();
  const [currentTooltip, setTooltip] = useState<number>(0);

  const rootNode = document?.document.nodes[document?.document.rootNodeId];
  const rootNodeType = rootNode?.type;
  const isHotspotGroup = rootNodeType === ContentType.HotspotGroup;
  const isTour = rootNodeType === ContentType.Tour;

  const isTypeWithTooltips = isHotspotGroup || isTour;

  const documentIsEmpty = Object.entries(document?.document?.nodes || {})?.length <= 1;

  const getDocumentsToRender = () => {
    if (!isTypeWithTooltips || documentIsEmpty) {
      return [document];
    }

    if (isTypeWithTooltips) {
      return rootNode?.nodes
        ?.filter((nodeId, index) => index === currentTooltip)
        ?.map((tooltipRootNodeId) => ({
          ...(document ?? {}),
          document: {
            ...document?.document,
            placements: adjustPlacementsToPreview(document?.document.placements || {}),
            rootNodeId: tooltipRootNodeId,
          },
        }));
    }

    const rootNodeId = (rootNode?.nodes ?? [])[0];
    if (!rootNodeId) {
      return null;
    }
    return [{ ...(document ?? {}), document: { ...(document?.document ?? {}), rootNodeId } }];
  };

  const documentsToRender = getDocumentsToRender();

  useEffect(() => {
    if (embedComponent.current) {
      Client.init({
        clientToken,
        userId,
        traits,
        options: {
          stylesheetSource: stylesheetFromSource ? 'api' : undefined,
          disableSegmentMembership,
          disableClientConfig,
          disableTracking: true,
          disableRenderLocations: true,
          disableFixedPosition: true,
          getTooltipMountPosition,
          getOverrideTooltipWrapperStyles,
        },
      });

      documentsToRender?.forEach((documentDefinition, index) => {
        const elementToRender = isTypeWithTooltips
          ? embedComponent.current.childNodes[index]
          : embedComponent.current;

        Client.renderContent({
          element: elementToRender,
          ...(slug ? { slug } : {}),
          ...(documentDefinition ? { documentDefinition } : {}),
        });
      });
    }
  }, [slug, document?.versionId, currentTooltip]);

  const isModal = rootNodeType === ContentType.Modal;

  return (
    <ContentThumbnailWithCarousel
      checkeredBackdrop={checkeredBackdrop}
      hotspotLimit={hotspotLimit}
      currentTooltip={currentTooltip}
      setTooltip={setTooltip}
      tooltipNodeIds={isTypeWithTooltips ? rootNode?.nodes || [] : undefined}
    >
      <div
        key={document?.versionId || slug}
        ref={embedComponent}
        css={{
          display: isTypeWithTooltips ? 'flex' : 'block',
          flexWrap: 'wrap',
          justifyContent: 'center',
          overflow: 'hidden',
          height: isModal || isTypeWithTooltips ? '625px' : '100%',
          zIndex: 1,
        }}
      >
        {isTypeWithTooltips &&
          rootNode?.nodes
            ?.filter((nodeId, index) => index === currentTooltip)
            .map((nodeId: string) => (
              <div
                key={nodeId}
                css={{
                  height: !hotspotLimit ? '100%' : '50%',
                  width: !hotspotLimit ? '100%' : '50%',
                  position: 'relative',
                  alignSelf: 'center',
                }}
              />
            ))}
      </div>
    </ContentThumbnailWithCarousel>
  );
};

export const CanduContentFrame = React.memo(
  ({
    isSdkPreview,
    showEmptyDocumentFallback,
    nodeId,
    checkeredBackdrop,
    ...props
  }: Props & {
    isSdkPreview?: boolean;
    showEmptyDocumentFallback?: boolean;
    nodeId?: string;
  }) => {
    const documentIsEmpty = Object.entries(props.document?.document?.nodes || {})?.length <= 1;

    if ((!props.document || documentIsEmpty) && showEmptyDocumentFallback) {
      return <EmptyPattern />;
    }

    if (!isSdkPreview) {
      return (
        <CanduContentFrameScreenshot
          nodeId={nodeId}
          checkeredBackdrop={
            checkeredBackdrop ||
            isTooltipDocument(
              props.document?.document?.nodes[props.document?.document?.rootNodeId].type,
            ) ||
            props.document?.document?.nodes[props.document?.document?.rootNodeId].type ===
              BuiltInComponents.Modal
          }
          {...props}
        />
      );
    }

    return (
      <CanduContentFrameInteractive
        checkeredBackdrop={
          checkeredBackdrop ||
          isTooltipDocument(
            props.document?.document?.nodes[props.document?.document?.rootNodeId].type,
          ) ||
          props.document?.document?.nodes[props.document?.document?.rootNodeId].type ===
            BuiltInComponents.Modal
        }
        {...props}
      />
    );
  },
);
